/**
 * useAbSelection - выбор эксперимента/групп для раздела «A/B тесты»
 * ====================================================================
 *
 * Выбор один на дашборд «Сравнение групп» и переживает перезагрузку страницы,
 * поэтому живёт в localStorage (по образцу `useRetentionGeo`), а не в
 * состоянии виджета.
 *
 * Значение: { experiment, groups: string[], control, horizonDays, maxLevel }.
 * `groups` — до 3 значений `ab`, `control` — одно из `groups`.
 */

import { useCallback, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';

const STORAGE_KEY = 'abtest:selection';
const DEFAULT_HORIZON_DAYS = 30;
const DEFAULT_MAX_LEVEL = 20;

const DEFAULT_SELECTION = {
  experiment: '',
  groups: [],
  control: '',
  horizonDays: DEFAULT_HORIZON_DAYS,
  maxLevel: DEFAULT_MAX_LEVEL
};

export function useAbSelection() {
  const [stored, setStored] = useLocalStorage(STORAGE_KEY, DEFAULT_SELECTION);

  const selection = useMemo(() => ({
    experiment: typeof stored?.experiment === 'string' ? stored.experiment : '',
    groups: Array.isArray(stored?.groups) ? stored.groups.filter((g) => typeof g === 'string') : [],
    control: typeof stored?.control === 'string' ? stored.control : '',
    horizonDays: Number(stored?.horizonDays) || DEFAULT_HORIZON_DAYS,
    maxLevel: Number(stored?.maxLevel) || DEFAULT_MAX_LEVEL
  }), [stored]);

  // Строковый ключ для массива зависимостей useWidgetData: сам объект/массив
  // пересоздаётся на каждый рендер и дёргал бы перезапрос вечно.
  const selectionKey = [
    selection.experiment,
    [...selection.groups].sort().join('|'),
    selection.control,
    selection.horizonDays,
    selection.maxLevel
  ].join('::');

  // Смена эксперимента сбрасывает группы и контроль на переданные значения
  // (обычно — все группы эксперимента + контроль по умолчанию из пикера).
  const setExperiment = useCallback(
    (experiment, { groups = [], control = '' } = {}) => {
      setStored((prev) => ({ ...prev, experiment, groups, control }));
    },
    [setStored]
  );

  const toggleGroup = useCallback(
    (group) => {
      setStored((prev) => {
        const list = Array.isArray(prev?.groups) ? prev.groups : [];
        const nextGroups = list.includes(group) ? list.filter((g) => g !== group) : [...list, group];
        // Контроль обязан оставаться среди выбранных групп
        const nextControl = nextGroups.includes(prev?.control) ? prev.control : (nextGroups[0] || '');
        return { ...prev, groups: nextGroups, control: nextControl };
      });
    },
    [setStored]
  );

  const setControl = useCallback(
    (control) => setStored((prev) => ({ ...prev, control })),
    [setStored]
  );

  const setHorizonDays = useCallback(
    (horizonDays) => setStored((prev) => ({ ...prev, horizonDays: Number(horizonDays) || DEFAULT_HORIZON_DAYS })),
    [setStored]
  );

  const setMaxLevel = useCallback(
    (maxLevel) => setStored((prev) => ({ ...prev, maxLevel: Number(maxLevel) || DEFAULT_MAX_LEVEL })),
    [setStored]
  );

  return {
    selection,
    selectionKey,
    setExperiment,
    toggleGroup,
    setControl,
    setHorizonDays,
    setMaxLevel
  };
}

export default useAbSelection;
